import { useCallback } from 'react';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';

import { Unarray } from '@pukka/common/types';
import { dateUtils, numberUtils } from '@pukka/common/utils';
import { TableV2, Text } from '@pukka/ui-components';
import { ColumnDef, Row, TableOptions, getExpandedRowModel, getGroupedRowModel } from '@tanstack/react-table';
import { useSnapshot } from 'valtio';

import { OldDataDisplay } from './OldDataDisplay';
import { SummaryHeader } from './SummaryHeader';
import {
  ITransactionSuccessState,
  SuccessTransactionState,
  TransactionPersistedState,
  TransactionState,
  useSuccessTransactionData,
} from './Transaction.state';
import { getIsDataTooOld } from './utils';

type IData = Unarray<ITransactionSuccessState['data']>;

const columns: ColumnDef<IData>[] = [
  {
    header: 'Outlet',
    accessorKey: 'outletName',
    cell(props) {
      return props.row.getIsGrouped() ? props.row.groupingValue : props.getValue();
    },
  },
  {
    header: 'Time',
    accessorFn(row) {
      return dateUtils.formatDate(row.date, 'HH:mm');
    },
  },
  {
    header: 'Collected By',
    accessorKey: 'by',
  },
  {
    header: 'Items',
    accessorKey: 'itemName',
    meta: {
      className: 'max-w-64',
    },
  },
  {
    accessorKey: 'group',
  },
  {
    header: 'Total Price',
    accessorKey: 'total',
    meta: {
      className: 'text-right',
    },
    cell(props) {
      return numberUtils.formatCurrency(props.getValue() as number, 'Rp.');
    },
  },
];

const options: Partial<TableOptions<IData>> = {
  getExpandedRowModel: getExpandedRowModel(),
  getGroupedRowModel: getGroupedRowModel(),
  groupedColumnMode: 'remove',
  initialState: {
    grouping: ['group'],
    expanded: true,
  },
  defaultColumn: {
    size: 50,
  },
};

function BasicNoDataPlaceholder() {
  return (
    <div>
      <Text variant="title-1" className="mb-4">
        No Transactions
      </Text>
      <Text color="neutral" className="mb-2">
        You can make transactions through your device, or select different time frame.
      </Text>
      <Text color="neutral">
        Learn more about{' '}
        <Text
          as="a"
          color="blue"
          target="_blank"
          className="hover:underline"
          href="https://help.mokapos.com/s/article/900000441803?language=in"
        >
          View and Download Your Customer Information and Transaction History
        </Text>
      </Text>
    </div>
  );
}

function SuccessTransactionPagelet() {
  const match = useMatch('v2/reports/transactions/:tabId/:token?');
  const token = match?.params.token as string;
  const { success } = useSnapshot(TransactionState);
  const { receipt, endDate, startDate } = useSnapshot(TransactionPersistedState);
  const { data, error, status } = useSnapshot(SuccessTransactionState);

  const navigate = useNavigate();

  const onRowClick = useCallback(
    (row: IData) => {
      navigate(`/v2/reports/transactions/success/${row.token}`, {
        unstable_viewTransition: true,
        preventScrollReset: true,
      });
    },
    [navigate],
  );

  const isRowHighlighted = useCallback(
    (row: Row<IData>) => {
      return row.original.token === token;
    },
    [token],
  );

  const summaryStatus = useSuccessTransactionData();

  const isDataTooOld = getIsDataTooOld(startDate, endDate);

  return (
    <>
      {receipt ? null : (
        <SummaryHeader loading={summaryStatus === 'loading'} className="mb-4 view-transition-[tx-success-header]">
          <SummaryHeader.Item title="TRANSACTIONS" sum={success?.count ?? 0} />
          <SummaryHeader.Item title="TOTAL COLLECTED" sum={success?.total ?? 0} inCurrencyFormat />
          <SummaryHeader.Item title="NET SALES" sum={success?.net ?? 0} inCurrencyFormat />
        </SummaryHeader>
      )}

      <div className="flex flex-row items-start">
        <div className="flex-1 view-transition-[tx-table]">
          <TableV2
            id="success-transactions"
            columns={columns}
            data={data as IData[]}
            bordered
            rounded
            options={options}
            className="table-fixed"
          >
            <TableV2.Header />
            {status === 'success' || data.length > 0 ? (
              <TableV2.Body
                isRowHighlighted={isRowHighlighted}
                onRowClick={onRowClick}
                noDataPlaceholder={isDataTooOld && data.length === 0 ? <OldDataDisplay /> : <BasicNoDataPlaceholder />}
              />
            ) : null}
            {status === 'loading' ? <TableV2.Loading /> : null}
            {status === 'error' ? <TableV2.Error>{error}</TableV2.Error> : null}
          </TableV2>
        </div>
        <Outlet />
      </div>
    </>
  );
}

export { SuccessTransactionPagelet };
